// Date and time utility functions for the BHA application

/**
 * Format a date string to a readable format
 * @param {string|Date} date - The date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };
  
  return dateObj.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Format a time string to a readable format
 * @param {string} time - The time to format (HH:MM format)
 * @param {boolean} use24Hour - Whether to use 24-hour format
 * @returns {string} Formatted time string
 */
export const formatTime = (time, use24Hour = false) => {
  if (!time) return '';
  
  // Handle both "HH:MM" and full datetime strings
  let timeString = time;
  if (time.includes('T') || time.includes(' ')) {
    const dateObj = new Date(time);
    if (isNaN(dateObj.getTime())) return '';
    timeString = dateObj.toTimeString().substring(0, 5);
  }
  
  if (use24Hour) {
    return timeString;
  }
  
  // Convert to 12-hour format
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  
  return `${displayHour}:${minutes} ${ampm}`;
};

/**
 * Format a full date and time
 * @param {string|Date} datetime - The datetime to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (datetime) => {
  if (!datetime) return '';
  
  const dateObj = new Date(datetime);
  if (isNaN(dateObj.getTime())) return '';
  
  return `${formatDate(dateObj)} at ${formatTime(dateObj)}`;
};

/**
 * Get relative time (e.g., "2 hours ago", "in 3 days")
 * @param {string|Date} date - The date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / (1000 * 60));
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  
  if (Math.abs(diffMins) < 60) {
    if (diffMins === 0) return 'now';
    return diffMins > 0 ? `in ${diffMins} minutes` : `${Math.abs(diffMins)} minutes ago`;
  }
  
  if (Math.abs(diffHours) < 24) {
    return diffHours > 0 ? `in ${diffHours} hours` : `${Math.abs(diffHours)} hours ago`;
  }
  
  if (Math.abs(diffDays) < 7) {
    return diffDays > 0 ? `in ${diffDays} days` : `${Math.abs(diffDays)} days ago`;
  }
  
  return formatDate(dateObj);
};

/**
 * Check if a date is today
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is today
 */
export const isToday = (date) => {
  if (!date) return false;
  
  const dateObj = new Date(date);
  const today = new Date();
  
  return dateObj.toDateString() === today.toDateString();
};

/**
 * Check if a date is in the future
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is in the future
 */
export const isFuture = (date) => {
  if (!date) return false;
  
  const dateObj = new Date(date);
  const now = new Date();
  
  return dateObj.getTime() > now.getTime();
};

/**
 * Check if a date is in the past
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is in the past
 */
export const isPast = (date) => {
  if (!date) return false;
  
  const dateObj = new Date(date);
  const now = new Date();
  
  return dateObj.getTime() < now.getTime();
};

/**
 * Get the current date in YYYY-MM-DD format
 * @returns {string} Current date string
 */
export const getCurrentDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

/**
 * Get the current time in HH:MM format
 * @returns {string} Current time string
 */
export const getCurrentTime = () => {
  const now = new Date();
  return now.toTimeString().substring(0, 5);
};

/**
 * Parse duration between two times
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @returns {string} Duration string (e.g., "2 hours 30 minutes")
 */
export const parseDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  
  const [startHours, startMins] = startTime.split(':').map(Number);
  const [endHours, endMins] = endTime.split(':').map(Number);
  
  let totalMins = (endHours * 60 + endMins) - (startHours * 60 + startMins);
  
  // Handle overnight duration
  if (totalMins < 0) {
    totalMins += 24 * 60;
  }
  
  const hours = Math.floor(totalMins / 60);
  const minutes = totalMins % 60;
  
  if (hours === 0) {
    return `${minutes} minutes`;
  } else if (minutes === 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
  }
};

/**
 * Add days to a date
 * @param {string|Date} date - The base date
 * @param {number} days - Number of days to add
 * @returns {Date} New date object
 */
export const addDays = (date, days) => {
  const dateObj = new Date(date);
  dateObj.setDate(dateObj.getDate() + days);
  return dateObj;
};

/**
 * Format date for input fields (YYYY-MM-DD)
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string for input
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toISOString().split('T')[0];
};

/**
 * Format time for input fields (HH:MM)
 * @param {string|Date} time - The time to format
 * @returns {string} Formatted time string for input
 */
export const formatTimeForInput = (time) => {
  if (!time) return '';
  
  if (typeof time === 'string' && time.includes(':')) {
    return time.substring(0, 5);
  }
  
  const dateObj = new Date(time);
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toTimeString().substring(0, 5);
};
